import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover'
import { defaultFilter } from 'cmdk'
import { Check, ChevronsUpDown } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { cn } from '@/utils'

type OptionType = { value: string; label: string }

/**
 * cmdk's own scoring, applied to the option's label only. Each item's `value` is its unique id
 * (cmdk keys highlighting and selection on `value`, so two options with the same label must not
 * share one), and the label travels as its first keyword, so typing never matches an id.
 */
function matchesLabel(_value: string, search: string, keywords?: string[]): number {
  return defaultFilter(keywords?.[0] ?? '', search)
}

interface SearchComboBoxProps {
  options: OptionType[]
  placeholder?: string
  /** Accessible name of the trigger. A combobox is not named by its text, so without it a screen
   *  reader announces an unnamed control. Read together with the shown value ("Grade 11th"). */
  ariaLabel?: string
  /** Shown in place of the list when the typed search matches nothing. Defaults to "No results
   *  found." */
  emptyMessage?: string
  /** The options are still loading / failed to load. Like `MultiSelectChipCombobox`, the dropdown
   *  shows `loadingMessage` / `errorMessage` instead of the list, so a request still running or
   *  failed is not mistaken for "nothing matched". */
  isLoading?: boolean
  isError?: boolean
  loadingMessage?: string
  errorMessage?: string
  /** Shows a "Try again" button beside the error message; called to request the options again. */
  onRetry?: () => void
  /** Controlled selected option value ('' = none, so the placeholder shows). Falls back to
   *  internal state when omitted, so uncontrolled callers keep working. */
  value?: string
  /** Called with the picked option's value. Single-select: picking replaces the selection, and
   *  picking the selected option again clears it (called with ''), the way the other trays'
   *  filter options toggle off. */
  onValueChange?: (value: string) => void
  /** Reports the popover's open state so a host can leave Escape to Radix while it's open. */
  onOpenChange?: (open: boolean) => void
}

/**
 * Single-select dropdown with its own "Search" box. The list is a shadcn `Command`, so typing
 * filters the options live with cmdk's built-in scoring (against each option's label); a long
 * list scrolls inside the dropdown (`CommandList` is `max-h-80 overflow-y-auto`). Opening it scrolls
 * the selected option into view, as `SelectCategoryComboBox` does.
 */
export function SearchComboBox({
  options,
  placeholder,
  ariaLabel,
  emptyMessage = 'No results found.',
  isLoading = false,
  isError = false,
  loadingMessage = 'Loading…',
  errorMessage = 'Failed to load.',
  onRetry,
  value,
  onValueChange,
  onOpenChange,
}: SearchComboBoxProps) {
  const labelId = useId()
  const listRef = useRef<HTMLDivElement>(null)
  const valueId = useId()
  const [open, setOpen] = useState(false)
  const [localValue, setLocalValue] = useState('')
  const selectedValue = value ?? localValue
  const selected = options.find((opt) => opt.value === selectedValue) ?? null

  // Opening a long list lands on the chosen option rather than the top of the list. Not left to
  // cmdk's own highlight-scroll, which follows keyboard/mouse hover, not "the chosen value": this
  // looks the option up by its own data attribute. Runs on the next frame, once the popover's
  // content is mounted.
  useEffect(() => {
    if (!open || !selectedValue) {
      return
    }

    const frame = requestAnimationFrame(() => {
      listRef.current
        ?.querySelector<HTMLElement>(`[data-combo-option="${CSS.escape(selectedValue)}"]`)
        ?.scrollIntoView({ block: 'nearest' })
    })

    return () => cancelAnimationFrame(frame)
  }, [open, selectedValue])

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)
    onOpenChange?.(nextOpen)
  }

  function handlePick(opt: OptionType) {
    const next = opt.value === selectedValue ? '' : opt.value

    setLocalValue(next)
    onValueChange?.(next)
    handleOpenChange(false)
  }

  return (
    <div>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-labelledby={ariaLabel ? `${labelId} ${valueId}` : undefined}
            aria-expanded={open}
            className="w-[var(--panel-dropdown-width)] h-9 px-3 flex justify-between items-center rounded-md border font-medium text-sm bg-card mb-2"
          >
            {ariaLabel ? (
              <span id={labelId} className="sr-only">
                {ariaLabel}
              </span>
            ) : null}
            <span
              id={valueId}
              title={selected?.label}
              className={cn(
                'truncate text-sm font-medium',
                selected ? 'text-foreground' : 'text-muted-foreground'
              )}
            >
              {selected ? selected.label : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 opacity-50 shrink-0" size={16} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--panel-dropdown-width)] px-3 p-0" align="start">
          <Command filter={matchesLabel}>
            <CommandInput placeholder="Search" autoFocus disabled={isLoading || isError} />
            <CommandList ref={listRef}>
              {isLoading && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {loadingMessage}
                </div>
              )}
              {isError && !isLoading && (
                <div className="flex flex-col items-center gap-2 px-3 py-6 text-center text-sm text-destructive">
                  <span>{errorMessage}</span>
                  {onRetry ? (
                    <button
                      type="button"
                      onClick={onRetry}
                      className="text-foreground underline underline-offset-2"
                    >
                      Try again
                    </button>
                  ) : null}
                </div>
              )}
              {!isLoading && !isError && (
                <>
                  <CommandEmpty>{emptyMessage}</CommandEmpty>
                  <CommandGroup>
                    {options.map((opt) => (
                      <CommandItem
                        key={opt.value}
                        value={opt.value}
                        data-combo-option={opt.value}
                        keywords={[opt.label]}
                        onSelect={() => handlePick(opt)}
                        className="flex items-center justify-between"
                      >
                        <span className="flex-1 truncate text-left" title={opt.label}>
                          {opt.label}
                        </span>
                        <Check
                          className={cn(
                            'ml-2 size-4 shrink-0',
                            opt.value === selectedValue ? 'opacity-100' : 'opacity-0'
                          )}
                          aria-hidden="true"
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
